---
title: 'Generate App Icons in Claude (App-Store-Ready)'
description: 'Generate a consistent, app-store-ready icon set from inside Claude or Cursor using the logo preset. Two-step workflow for transparent corners.'
slug: generate-app-icons-claude
category: use-case
pillar: E
primaryKeyword: 'AI app icon generator'
audience: dev
date: 2026-05-30
updated: 2026-05-30
author: 'AgentBrush Team'
ogImage: /og/blog/generate-app-icons-claude.png
canonical: ''
schema: HowTo
featured: false
draft: false
relatedSlugs:
  - install-agentbrush-claude-cursor
  - transparent-background-png-generator
  - agentbrush-prompting-guide
---

You can generate a production-ready app icon without leaving your editor: ask Claude or Cursor to call AgentBrush with a `logo` preset, get a clean 1024x1024 PNG, strip the background for transparent corners, and export the store sizes. A few agent messages, start to finish.

This guide covers the single-icon workflow, keeping a set consistent with a reference image, and how to describe an icon so it survives scaling.

## Why the `logo` preset is the right starting point for an AI app icon generator

Of AgentBrush's six presets, `logo` is the one tuned for marks and icons: clean shapes, crisp edges, high contrast. Those are the properties that survive scaling from a 1024px master down to a 29px notification badge. The expressive presets add texture and gradients that go muddy at small sizes.

Use `logo` as your default. Switch to `flat_illustration` for a figurative scene inside the canvas, or `pixel_art` for retro games where jagged edges are the point.

## The single-icon workflow

### Step 1: Generate on an opaque background

Ask for an opaque background in a solid color you pick, not transparency. The model fills the canvas more reliably, and step 2 is what gives you the transparent corners. Asking for transparency at generation time is unreliable with every current model, `gpt-image-2` included.

```text
Generate a minimal app icon for a productivity app called "Pulse".
The icon is a stylized heartbeat waveform inside a rounded-square badge,
flat geometric style, dark navy background (#0B1120), waveform in electric cyan,
bold and legible at small sizes. Leave no decorative noise.
preset: logo · quality: high · size: 1024x1024 · background: opaque
```

<Figure src="images/app-icon-pulse.png" alt="The Pulse app icon: a dark navy rounded-square badge with a bold electric-cyan heartbeat waveform, flat and geometric." priority />

High quality is 20 tokens. Draft at low (1 token) first, then commit to high once the direction is right.

### Step 2: Remove the background for transparent corners

The badge does not fill a square canvas, so you need transparent corners for the OS to apply its own mask (iOS squircle, Android adaptive shape).

```text
Remove the background from the Pulse icon.
```

Removal runs locally, costs 0 tokens, and outputs a real alpha channel: transparent outside the badge, opaque inside. Bold, high-contrast marks cut cleanest. More on the two-step approach in [how to generate a transparent-background PNG](/blog/transparent-background-png-generator).

### Step 3: Export to the sizes each store needs

Both stores take one large master and generate the rest, but knowing the targets lets you check the output holds up at every size.

**Apple App Store / iOS:**

| Use                | Size                       |
| ------------------ | -------------------------- |
| App Store listing  | 1024x1024                  |
| iPhone home screen | 60x60 (@2x: 120, @3x: 180) |
| iPad home screen   | 76x76 (@2x: 152)           |
| Spotlight          | 40x40 (@2x: 80, @3x: 120)  |
| Notification       | 20x20 (@2x: 40, @3x: 60)   |

**Google Play / Android:**

| Use                | Size    |
| ------------------ | ------- |
| Play Store listing | 512x512 |
| Launcher (hdpi)    | 48x48   |
| Launcher (xhdpi)   | 72x72   |
| Launcher (xxhdpi)  | 96x96   |
| Launcher (xxxhdpi) | 192x192 |

Your 1024x1024 master covers all of it. Resize programmatically (ImageMagick, Sharp, Xcode, Android Studio); that is a two-line script, not an AI job. One check: at 29-40px, confirm the mark is still readable. Bold single-element marks survive; busy compositions do not.

## Keep a matching icon set consistent

A single icon is easy; a matching set is where consistency breaks if you generate each one independently. Register your first approved icon as a reference image and pass it to every later generation. The model reads the visual language (shapes, palette, badge style, line weight) from the reference and applies it to the new subject.

```text
Generate an app icon for "Pulse Analytics", a companion analytics module.
This icon should show a minimal bar-chart mark, same badge shape, same dark navy background,
same electric-cyan accent color, same flat geometric style as the reference.
preset: logo · quality: high · size: 1024x1024
reference_image_paths: ["assets/icons/pulse-icon.png"]
```

<Figure src="images/app-icon-suite-no-bg.png" alt="Four matching app icons (a heartbeat waveform, a bar chart, a calendar, a document) sharing a navy badge and cyan marks, with transparent corners shown on a checkerboard." transparent />

Two catches. The reference biases toward replicating the input, not improving it, so nail icon one before anchoring the set to it. And keep the follow-up prompts short: a reference is a strong influence, and a long prompt fights it.

## Prompt anatomy for better icon results

**Name the badge shape.** "Rounded-square badge" or "circular badge" sets the container; without it, shapes drift across a set.

**Give exact hex colors.** `#0B1120` and `#00E5FF` are unambiguous. "Dark blue and cyan" varies run to run.

**Describe the mark by function, not metaphor.** "A flat line that spikes once and returns to flat" beats "a health icon."

**Ask for simplicity.** "Bold and legible at small sizes", "no decorative noise", "single focal element" push toward marks that scale. Every detail you add appears, and detail that reads at 1024px vanishes at 60px.

**Draft low, commit high.** At 1024x1024: low is 1 token, medium 5, high 20. Background removal is always 0.

## Where this breaks down

**Detailed mascots inside icons** lose legibility when small. Simplify a character to a silhouette.

**Nudging small details is imprecise.** To tweak padding or a line weight, use the [mask editor](/blog/mask-editor-inpaint-iterate), not a full regenerate.

**Icons above 2K are experimental.** 1024x1024 is the right master; you do not need larger.

**The reference needs an approved first icon.** Fix icon one before anchoring the set, or you re-anchor every icon after it.

## Getting set up

AgentBrush is an MCP server: connect it once to Claude Code, Cursor, Codex CLI, Gemini CLI, or any MCP-compatible client, and every icon request goes through it. The [install guide](/blog/install-agentbrush-claude-cursor) is a two-minute setup. Starter ($6.99/month, 100 tokens) covers a full icon-set cycle; Pro ($14.99/month, 600 tokens) makes sense if you do this regularly. For describing what you want, the [prompting guide](/blog/agentbrush-prompting-guide) goes deeper.

## FAQ

**Can I use these icons commercially, including App Store and Play Store submissions?**
Yes. Images generated via gpt-image-2 are yours to use commercially. Trademark and third-party IP rules still apply: do not ask the model to reproduce another app's icon or a trademarked logo.

**Do I need my own OpenAI API key?**
No. AgentBrush handles the gpt-image-2 calls under your subscription. You never touch a key.

**What is the right master size to hand to Xcode or Android Studio?**
1024x1024 PNG with transparent corners (step 2). Both tools generate every derivative size from it.

**Can I generate an iOS adaptive icon or Android foreground/background layer pair?**
Android adaptive icons need a foreground on transparent plus a separate background layer: generate the mark on transparent (the two-step workflow) and a solid-color square. iOS handles the squircle from your 1024x1024 master.

---

**Ready to generate your first icon?** [Connect AgentBrush to your agent](/getting-started), paste the Pulse prompt above, and your master PNG lands in your project in seconds.
